import React from 'react'
import styled, { css } from 'styled-components'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import isNaN from 'lodash/isNaN'

import { ppsSelector } from '../../../selectors'

import Typography from '../../../lib/typography'
import { createTableCard } from '../../../components/project-profit-sheet/TableCard'
import IndicatorGP, * as IndicatorGPLayout from '../../../components/project-profit-sheet/IndicatorGP'

import { selectMode } from '../model'
import { parseFinancial } from '../../../lib/utils'

const BaseCard = createTableCard(css`
  grid-template-columns: repeat(3, calc(50% / 3)) 50%;
`)

const Card = {
  ...BaseCard,
  Container: styled(BaseCard.Container)`
    height: 70px;
    padding-bottom: 12px;
    align-items: end;

    ${IndicatorGPLayout.Bar} {
      width: 95%;
      margin-left: 8px;
      grid-column: span 2;
    }
  `,
  TypographyCol: styled(Typography.Data)`
    flex: ${p => p.flex || 1};
  `,
  Collapse: styled.div`
    display: flex;
    flex-direction: column;
  `,
  Wrapper: styled.div`
    display: flex;
    flex-direction: column;
  `,
}

const fallbackNaN = (number) => (isNaN(number) ? '0.00' : number)

function GPSummaryContainer({ profitData: o, display }) {
  const mode = selectMode(display)
  // const data = {
  //   forecast: o[`$${mode}_overall_summary_gross_profit_hardware_forecast`],
  //   actual: o[`$${mode}_overall_summary_gross_profit_service_actual`],
  //   remaining: o[`$${mode}_overall_summary_gross_profit_service_remaining`],
  //   initial: o[`$${mode}_overall_summary_gross_profit_service_initial_plan`],
  //   current: o['$price_cost_current_price'],
  // }
  const service = {
    name: 'GP Service (+ Other cost)',
    forecast: o[`$${mode}_overall_summary_gross_profit_service_forecast`] || '-',
    actual: o[`$${mode}_overall_summary_gross_profit_service_actual`] || '-',
    remaining: o[`$${mode}_overall_summary_gross_profit_service_remaining`] || '-',
    initial: o[`$${mode}_overall_summary_gross_profit_service_initial_plan`] || '-',
    current_price: o[`$service_price`] || '-',
  }
  const vendor = {
    name: 'GP Other Service',
    forecast: o[`$overall_summary_gross_profit_vendor_forecast`] || '-',
    actual: o[`$overall_summary_gross_profit_vendor_actual`] || '-',
    remaining: o[`$overall_summary_gross_profit_vendor_remaining`] || '-',
    initial: o[`$overall_summary_gross_profit_vendor_initial_plan`] || '-',
    current_price: o[`$vendor_price`] || '-',
  }
  const hardware = {
    name: 'GP Hardware',
    forecast: o[`$overall_summary_gross_profit_hardware_forecast`] || '-',
    actual: o[`$overall_summary_gross_profit_hardware_actual`] || '-',
    remaining: o[`$overall_summary_gross_profit_hardware_remaining`] || '-',
    initial: o[`$overall_summary_gross_profit_hardware_initial_plan`] || '-',
    current_price: o[`$hardware_price`] || '-',
  }
  const hardware_ma = {
    name: 'GP HW/MA',
    forecast: o[`$overall_summary_gross_profit_hardware_ma_forecast`] || '-',
    actual: o[`$overall_summary_gross_profit_hardware_ma_actual`] || '-',
    remaining: o[`$overall_summary_gross_profit_hardware_ma_remaining`] || '-',
    initial: o[`$overall_summary_gross_profit_hardware_ma_initial_plan`] || '-',
    current_price: o[`$hardware_ma_price`] || '-',
  }
  const license = {
    name: 'GP License',
    forecast: o[`$overall_summary_gross_profit_license_forecast`] || '-',
    actual: o[`$overall_summary_gross_profit_license_actual`] || '-',
    remaining: o[`$overall_summary_gross_profit_license_remaining`] || '-',
    initial: o[`$overall_summary_gross_profit_license_initial_plan`] || '-',
    current_price: o[`$license_price`] || '-',
  }
  const license_ma = {
    name: 'GP License/MA',
    forecast: o[`$overall_summary_gross_profit_license_ma_forecast`] || '-',
    actual: o[`$overall_summary_gross_profit_license_ma_actual`] || '-',
    remaining: o[`$overall_summary_gross_profit_license_ma_remaining`] || '-',
    initial: o[`$overall_summary_gross_profit_license_ma_initial_plan`] || '-',
    current_price: o[`$license_ma_price`] || '-',
  }
  const subscription = {
    name: 'GP Subscription',
    forecast: o[`$overall_summary_gross_profit_subscription_forecast`] || '-',
    actual: o[`$overall_summary_gross_profit_subscription_actual`] || '-',
    remaining: o[`$overall_summary_gross_profit_subscription_remaining`] || '-',
    initial: o[`$overall_summary_gross_profit_subscription_initial_plan`] || '-',
    current_price: o[`$subscription_price`] || '-',
  }
  const types = [
    service,
    vendor,
    hardware,
    hardware_ma,
    license,
    license_ma,
    subscription,
  ]

  return (
    <Card.Root topic="GP Summary">
      <Card.Collapse>
        <Card.Heading>
          <Card.TypographyCol>Gross Profit</Card.TypographyCol>
          <Card.TypographyCol>Forecast GP</Card.TypographyCol>
          <Card.TypographyCol style={ { paddingLeft: 8 } }>Approved Plan GP</Card.TypographyCol>
          <Card.TypographyCol flex={ 3 } style={ { paddingLeft: 8 } }>Progress</Card.TypographyCol>
        </Card.Heading>
        {types.map((data, key) => {
          const statusColor = parseFinancial(data.forecast) >= 0 ? 'green' : 'red'
          return (
            <Card.Wrapper key={ key }>
              <Card.Container>
                <Typography.Data /* disc={ statusColor } */>{ data.name }</Typography.Data>
                <Typography.Data disc={ statusColor }>฿ {fallbackNaN(data.forecast)}</Typography.Data>
                <Typography.Data disc="black" weight="normal">
                        ฿ {fallbackNaN(data.initial)}
                </Typography.Data>
                <div>
                  <IndicatorGP
                    current_price={ parseFinancial(data.current_price) }
                    approved_plan={ parseFinancial(data.initial) }
                    forecast={ parseFinancial(data.forecast) }
                    actual={ parseFinancial(data.actual) }
                    remaining={ parseFinancial(data.remaining) }
                  />
                </div>
              </Card.Container>
            </Card.Wrapper>
          )
        }
        )}
      </Card.Collapse>
    </Card.Root>
  )
}

const mapStateToProps = createStructuredSelector({
  profitData: ppsSelector.profitData,
  display: ppsSelector.display,
})

export default connect(mapStateToProps)(GPSummaryContainer)



// WEBPACK FOOTER //
// ./src/pages/project-profit-sheet/containers/GPSummaryContainer.js